import { ApiService, ConverterService, CredentialsService, EndToEndEncrypter, ErrorsService } from '../../providers';
import { AppRoute } from '../../enums';
import { CredentialError } from '../../types';
import { Injectable, inject, signal } from '@angular/core';
import { MessageBase } from '../index';
import { Router } from '@angular/router';
import { Stammdaten } from '../../models';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class StammdatenService {
  private readonly api = inject(ApiService);
  private readonly converterService = inject(ConverterService);
  private readonly credentialsService = inject(CredentialsService);
  private readonly endToEndEncrypter = inject(EndToEndEncrypter);
  private readonly errorsService = inject(ErrorsService);
  private readonly message = inject(MessageBase);
  private readonly router = inject(Router);

  private static readonly keysToExclude = new Set(['aktualisiertUtc', 'angelegtUtc', 'id', 'token']);
  public stammdaten = signal(new Stammdaten());
  public async getStammdaten(): Promise<Stammdaten | undefined> {
    const stammdaten = await this.getDecryptedStammdaten();
    if (!stammdaten) {
      // something went wrong
      return undefined;
    }
    this.stammdaten.set(stammdaten);
    return stammdaten;
  }

  private async decryptStammdaten(stammdaten: Stammdaten): Promise<Stammdaten | undefined> {
    if (stammdaten === null) {
      throw new Error('Cannot decrypt stammdaten because they are null');
    }

    const copy = new Stammdaten();
    for (const [key, value] of Object.entries(stammdaten)) {
      if (StammdatenService.keysToExclude.has(key) || !value) {
        copy[key as keyof typeof stammdaten] = value;
        continue;
      }

      let val;
      try {
        val = await this.decryptFieldValue(key, value as string | Blob);
        copy[key] = val as string;
      } catch (error) {
        const asError = this.normalizeError(error);
        await this.handleEncryptionIssues(asError);
        throw error;
      }

      if (key === 'personalnummer') {
        const hasOnlyNumbers = /^\d+$/.test(copy[key]);
        // edge case on iOS - encryption succeeds, but with wrong encryption, we know personal number has to be numbers only
        if (!hasOnlyNumbers) {
          await this.handleEncryptionIssues(new CredentialError('PIN', 'pin not available'));
          return undefined;
        }
      }
    }

    return copy;
  }

  private async decryptFieldValue(key: string, value: string | Blob): Promise<string> {
    const uintValue =
      key === 'personalnummer' || key === 'gebdat'
        ? await this.endToEndEncrypter.decryptToUint8(value)
        : await this.endToEndEncrypter.decryptToUint16(value);

    let convertedValue = this.converterService.uIntArrayToString(uintValue as Uint16Array | Uint8Array | ArrayBuffer);

    // Backend may deliver "gebdat" in UTF-16. Decoding that payload as Uint8 injects \u0000 separators.
    if (key === 'gebdat' && convertedValue.includes('\u0000')) {
      const utf16Value = await this.endToEndEncrypter.decryptToUint16(value);
      convertedValue = this.converterService.uIntArrayToString(utf16Value);
    }

    return convertedValue;
  }

  private async getDecryptedStammdaten(): Promise<Stammdaten | undefined> {
    try {
      const stammdaten = await this.getStammdatenFromBackend();
      return this.decryptStammdaten(stammdaten);
    } catch (error) {
      const asError = this.normalizeError(error);
      await this.handleEncryptionIssues(asError);
      return undefined;
    }
  }

  private normalizeError(err: unknown): Error {
    if (this.errorsService.isServerResponse(err)) {
      return new Error(err.message);
    }

    if (err instanceof Error && err.name === 'OperationError') {
      return new Error('encryption_error');
    }

    if (err instanceof Error) {
      return err;
    }

    return new Error(JSON.stringify(err));
  }

  private getStammdatenFromBackend(): Promise<Stammdaten> {
    return firstValueFrom(this.api.restGet<Stammdaten>('data/stammdaten'));
  }

  private async handleEncryptionIssues(error: Error): Promise<boolean> {
    const errorMessage = error.message.trim();

    if (errorMessage.endsWith('encryption_error')) {
      await this.message.showError({
        message: _('view_finished_antrag.text.encryption_error'),
      });
    }

    if (error instanceof CredentialError && error.reason === 'PIN') {
      console.log('pin error, resetting pin and navigate to validation page', errorMessage);

      // issue with incorrect PIN, navigate to PIN Enter site
      await this.resetPin();
      return this.router.navigate([AppRoute.PinValidation]);
    }

    if (errorMessage.endsWith('NoEntries') && errorMessage.includes('stammdaten')) {
      await this.message.showError({ message: _('server.error.get_stammdaten.NoEntries') });
      await this.router.navigate(['/', AppRoute.DashboardPage]);
    }

    if (
      errorMessage.endsWith('missingData') ||
      (errorMessage.includes('get_stammdaten') && errorMessage.endsWith('ActionPending'))
    ) {
      await this.message.showInfo({ message: errorMessage });
      return this.router.navigate([AppRoute.DashboardPage]);
    }

    // some other error with encryption, possibly pwd or PIN (not handled correctly)
    // we should log out and remove PIN, so that the user has to enter both correct
    console.log('encryption error, resetting pin and navigate to logout page', errorMessage);

    await this.resetPin();
    return this.router.navigate([AppRoute.Logout]);
  }

  private resetPin(): Promise<void> {
    return this.credentialsService.resetPin();
  }
}
